import { createAppTour } from "@/utils/tour";

export const QUICK_GUIDE_NAVIGATE_EVENT = "quick-guide:navigate";

export const QUICK_GUIDE_STEPS = [
  {
    id: "bug-report",
    number: 1,
    title: "Report a Bug or Suggestion",
    action: "bug-report-dialog",
  },
  {
    id: "profile",
    number: 2,
    title: "Your Profile",
    route: "/profile",
    query: { quickGuide: "profile" },
  },
  {
    id: "more-options",
    number: 3,
    title: "More Options",
    action: "more-options-menu",
  },
  {
    id: "customize-themes",
    number: 4,
    title: "Themes",
    action: "themes-dialog",
  },
  {
    id: "email-templates",
    number: 5,
    title: "Email Template",
    action: "email-template-dialog",
  },
  {
    id: "team-requests",
    number: 6,
    title: "Requests",
    action: "team-requests-drawer",
  },
  {
    id: "broker-oversight",
    number: 7,
    title: "Send Request",
    route: "/sendrequests",
    query: { quickGuide: "broker-oversight" },
  },
  {
    id: "verify-email",
    number: 8,
    title: "Reply-To Email",
    route: "/profile",
    query: { quickGuide: "verify-email" },
  },
  {
    id: "create-team",
    number: 9,
    title: "Create a Team",
    route: "/team",
    query: { quickGuide: "create-team" },
  },
  {
    id: "upload-contract-ai",
    number: 10,
    title: "Create a Checklist",
    route: "/checklist",
    query: { quickGuide: "upload-contract-ai" },
  },
  {
    id: "contract-template",
    number: 11,
    title: "Checklist Templates",
    route: "/template",
    query: { quickGuide: "contract-template" },
  },
  {
    id: "send-contract",
    number: 12,
    title: "Share Checklist",
    route: "/checklist/tour-demo",
    query: { quickGuide: "send-contract" },
  },
];

const STORAGE_PREFIX = "quickGuidesCompleted_";
const ACTIVE_GUIDE_KEY = "activeQuickGuide";
const AUTO_SHOWN_PREFIX = "quickGuidesAutoShown_";
const REOPEN_MENU_KEY = "quickGuideReopenMenu";

export const QUICK_GUIDE_REOPEN_EVENT = "quick-guide:reopen-menu";
export const QUICK_GUIDE_LAUNCH_STEP_EVENT = "quick-guide:launch-step";
export const QUICK_GUIDE_OPEN_EMAIL_TEMPLATE_EVENT =
  "quick-guide:open-email-template";
export const QUICK_GUIDE_OPEN_THEMES_EVENT = "quick-guide:open-themes";
export const QUICK_GUIDE_OPEN_BUG_REPORT_EVENT = "quick-guide:open-bug-report";
export const QUICK_GUIDE_OPEN_MORE_OPTIONS_EVENT =
  "quick-guide:open-more-options";
export const QUICK_GUIDE_OPEN_REQUESTS_EVENT = "quick-guide:open-requests";

const ADMIN_HEADER_ROUTES = [
  "/checklist",
  "/template",
  "/team",
  "/notification",
  "/chats",
  "/member",
];

let quickGuideRouter = null;

export function registerQuickGuideRouter(router) {
  quickGuideRouter = router;
}

function hasAdminHeaderRoute(path) {
  if (!path) return false;
  return ADMIN_HEADER_ROUTES.some(
    (routePath) => path === routePath || path.startsWith(`${routePath}/`)
  );
}

export function launchQuickGuideStep(step) {
  if (!step) return;

  destroyQuickGuideSpotlight();
  enableQuickGuideReopen();
  setActiveQuickGuide(step.id);

  const openDialogAction = (eventName) => {
    const open = () => {
      window.dispatchEvent(new CustomEvent(eventName));
    };

    if (
      quickGuideRouter &&
      hasAdminHeaderRoute(quickGuideRouter.currentRoute.path)
    ) {
      open();
      return;
    }

    if (quickGuideRouter) {
      quickGuideRouter.push("/checklist").then(() => {
        setTimeout(open, 450);
      });
      return;
    }

    open();
  };

  if (
    step.action === "email-template-dialog" ||
    step.id === "email-templates"
  ) {
    openDialogAction(QUICK_GUIDE_OPEN_EMAIL_TEMPLATE_EVENT);
    return;
  }

  if (step.action === "themes-dialog" || step.id === "customize-themes") {
    openDialogAction(QUICK_GUIDE_OPEN_THEMES_EVENT);
    return;
  }

  if (step.action === "bug-report-dialog" || step.id === "bug-report") {
    openDialogAction(QUICK_GUIDE_OPEN_BUG_REPORT_EVENT);
    return;
  }

  if (step.action === "more-options-menu" || step.id === "more-options") {
    openDialogAction(QUICK_GUIDE_OPEN_MORE_OPTIONS_EVENT);
    return;
  }

  if (step.action === "team-requests-drawer" || step.id === "team-requests") {
    openDialogAction(QUICK_GUIDE_OPEN_REQUESTS_EVENT);
    return;
  }

  const routeTarget = buildQuickGuideRoute(step);
  if (!routeTarget || !quickGuideRouter) return;

  const notify = () => {
    setTimeout(() => dispatchQuickGuideNavigate(step.id), 150);
  };

  if (quickGuideRouter.currentRoute.path === routeTarget.path) {
    quickGuideRouter.replace(routeTarget).then(notify).catch(notify);
    return;
  }

  quickGuideRouter.push(routeTarget).then(notify).catch(notify);
}

export function enableQuickGuideReopen() {
  sessionStorage.setItem(REOPEN_MENU_KEY, "true");
}

export function disableQuickGuideReopen() {
  sessionStorage.removeItem(REOPEN_MENU_KEY);
}

export function shouldReopenQuickGuideMenu() {
  return sessionStorage.getItem(REOPEN_MENU_KEY) === "true";
}

export function requestReopenQuickGuideMenu() {
  if (!shouldReopenQuickGuideMenu()) return;
  window.dispatchEvent(new CustomEvent(QUICK_GUIDE_REOPEN_EVENT));
}

export function navigateToTeamAndOpenQuickGuides(router) {
  if (!router) {
    enableQuickGuideReopen();
    requestReopenQuickGuideMenu();
    return;
  }

  enableQuickGuideReopen();
  const target = {
    path: "/team",
    query: { ...(router.currentRoute?.query || {}), openQuickGuides: "1" },
  };
  // Drop active guide query so spotlight does not restart
  delete target.query.quickGuide;

  const go = () =>
    router.currentRoute?.path === "/team"
      ? router.replace(target)
      : router.push(target);

  go().catch(() => {
    requestReopenQuickGuideMenu();
  });
}

export function clearQuickGuideRoute(router, route) {
  if (!router || !route?.query?.quickGuide) return;
  const query = { ...route.query };
  delete query.quickGuide;
  router.replace({ path: route.path, query }).catch(() => {});
}

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId || "guest"}`;
}

function autoShownKey(userId) {
  return `${AUTO_SHOWN_PREFIX}${userId || "guest"}`;
}

export function backupPersistentQuickGuideStorage() {
  const backup = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key &&
      (key.startsWith(STORAGE_PREFIX) || key.startsWith(AUTO_SHOWN_PREFIX))
    ) {
      backup[key] = localStorage.getItem(key);
    }
  }
  return backup;
}

export function restorePersistentQuickGuideStorage(backup) {
  if (!backup || typeof backup !== "object") return;
  Object.entries(backup).forEach(([key, value]) => {
    if (value != null) {
      localStorage.setItem(key, value);
    }
  });
}

export function clearLocalStoragePreservingQuickGuides() {
  const backup = backupPersistentQuickGuideStorage();
  localStorage.clear();
  restorePersistentQuickGuideStorage(backup);
}

export function hasQuickGuidesAutoShown(userId) {
  if (
    userId &&
    getCompletedQuickGuides(userId).length === QUICK_GUIDE_STEPS.length
  ) {
    return true;
  }
  return localStorage.getItem(autoShownKey(userId)) === "true";
}

export function markQuickGuidesAutoShown(userId) {
  localStorage.setItem(autoShownKey(userId), "true");
}

export function getCompletedQuickGuides(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function isQuickGuideComplete(userId, guideId) {
  return getCompletedQuickGuides(userId).includes(guideId);
}

export function completeQuickGuide(userId, guideId) {
  if (!guideId) return;
  const completed = getCompletedQuickGuides(userId);
  if (completed.includes(guideId)) return;
  completed.push(guideId);
  localStorage.setItem(storageKey(userId), JSON.stringify(completed));
  if (completed.length === QUICK_GUIDE_STEPS.length) {
    markQuickGuidesAutoShown(userId);
  }
  window.dispatchEvent(
    new CustomEvent("quick-guides-updated", { detail: { guideId } })
  );
}

export function getQuickGuidesWithProgress(userId) {
  const completed = getCompletedQuickGuides(userId);
  return QUICK_GUIDE_STEPS.map((step) => ({
    ...step,
    completed: completed.includes(step.id),
  }));
}

export function setActiveQuickGuide(guideId) {
  if (guideId) {
    localStorage.setItem(ACTIVE_GUIDE_KEY, guideId);
  } else {
    localStorage.removeItem(ACTIVE_GUIDE_KEY);
  }
}

export function getActiveQuickGuide() {
  return localStorage.getItem(ACTIVE_GUIDE_KEY);
}

export function tryCompleteActiveQuickGuide(userId, route) {
  const guideId =
    (route && route.query && route.query.quickGuide) || getActiveQuickGuide();
  if (guideId && isQuickGuideId(guideId)) {
    completeQuickGuide(userId, guideId);
    setActiveQuickGuide(null);
  }
}

export function isQuickGuideId(value) {
  return QUICK_GUIDE_STEPS.some((step) => step.id === value);
}

export function getQuickGuideById(guideId) {
  return QUICK_GUIDE_STEPS.find((step) => step.id === guideId) || null;
}

export function areAllQuickGuidesComplete(userId) {
  return QUICK_GUIDE_STEPS.every((step) => isQuickGuideComplete(userId, step.id));
}

export function getNextIncompleteQuickGuide(
  userId,
  currentGuideId,
  { treatCurrentAsComplete = false } = {}
) {
  const completed = getCompletedQuickGuides(userId);
  const isDone = (id) =>
    completed.includes(id) ||
    (treatCurrentAsComplete && id === currentGuideId);

  if (!currentGuideId) {
    return QUICK_GUIDE_STEPS.find((step) => !completed.includes(step.id)) || null;
  }

  const currentIndex = QUICK_GUIDE_STEPS.findIndex(
    (step) => step.id === currentGuideId
  );
  if (currentIndex === -1) {
    return QUICK_GUIDE_STEPS.find((step) => !isDone(step.id)) || null;
  }

  for (let i = currentIndex + 1; i < QUICK_GUIDE_STEPS.length; i++) {
    if (!isDone(QUICK_GUIDE_STEPS[i].id)) return QUICK_GUIDE_STEPS[i];
  }
  for (let i = 0; i < currentIndex; i++) {
    if (!isDone(QUICK_GUIDE_STEPS[i].id)) return QUICK_GUIDE_STEPS[i];
  }
  return null;
}

export function getPreviousQuickGuide(currentGuideId) {
  const currentIndex = QUICK_GUIDE_STEPS.findIndex(
    (step) => step.id === currentGuideId
  );
  if (currentIndex <= 0) return null;
  return QUICK_GUIDE_STEPS[currentIndex - 1];
}

export function launchNextIncompleteQuickGuide(userId, currentGuideId) {
  const next = getNextIncompleteQuickGuide(userId, currentGuideId, {
    treatCurrentAsComplete: true,
  });
  if (!next) return false;
  launchQuickGuideStep(next);
  return true;
}

export function launchPreviousQuickGuide(currentGuideId) {
  const prev = getPreviousQuickGuide(currentGuideId);
  if (!prev) return false;
  launchQuickGuideStep(prev);
  return true;
}

export function resolveQuickGuideContext(vm) {
  const userId =
    vm?.$store?.getters?.getUserId ||
    vm?.getUserId ||
    localStorage.getItem("userId");
  const guideId = vm?.$route?.query?.quickGuide || getActiveQuickGuide();
  return { userId, guideId };
}

export function dispatchQuickGuideNavigate(guideId) {
  if (!guideId) return;
  window.dispatchEvent(
    new CustomEvent(QUICK_GUIDE_NAVIGATE_EVENT, { detail: { guideId } })
  );
}

export function bindQuickGuideNavigation(vm, handler) {
  if (!vm || typeof handler !== "function") return () => {};

  const onEvent = (event) => {
    const guideId = event?.detail?.guideId;
    if (guideId) handler(guideId);
  };

  const routeWatcher = vm.$watch(
    () => vm.$route && vm.$route.query && vm.$route.query.quickGuide,
    (guideId) => {
      if (guideId) handler(guideId);
    }
  );

  window.addEventListener(QUICK_GUIDE_NAVIGATE_EVENT, onEvent);

  if (vm.$route?.query?.quickGuide) {
    handler(vm.$route.query.quickGuide);
  }

  return () => {
    routeWatcher();
    window.removeEventListener(QUICK_GUIDE_NAVIGATE_EVENT, onEvent);
  };
}

export function buildQuickGuideRoute(step) {
  if (!step || !step.route) return null;
  return {
    path: step.route,
    query: {
      ...(step.query || {}),
      quickGuide: step.id,
    },
  };
}

let activeGuideDriver = null;

export function destroyQuickGuideSpotlight() {
  if (activeGuideDriver && typeof activeGuideDriver.destroy === "function") {
    try {
      activeGuideDriver.destroy();
    } catch (error) {
      // ignore teardown errors from an already-closed driver instance
    }
  }
  activeGuideDriver = null;

  document
    .querySelectorAll(".driver-overlay, .driver-popover, .driver-active-element")
    .forEach((node) => {
      try {
        if (node && node.parentNode) {
          node.parentNode.removeChild(node);
        } else if (node && typeof node.remove === "function") {
          node.remove();
        }
      } catch (e) {
        // safe ignore
      }
    });
  document.body.classList.remove("driver-active");
  document.documentElement.classList.remove("driver-active");
}

export function runQuickGuideTour({
  steps = [],
  onComplete,
  onDismiss,
  side = "bottom",
  userId,
  guideId,
  startDelay = 0,
}) {
  destroyQuickGuideSpotlight();

  const waitForVisibleElements = (selectors, maxWait = 4000) =>
    new Promise((resolve) => {
      const deadline = Date.now() + maxWait;

      const check = () => {
        const allReady = selectors.every((selector) => {
          const element = document.querySelector(selector);
          if (!element) return false;
          const rect = element.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        });

        if (allReady || Date.now() >= deadline) {
          resolve(allReady);
          return;
        }

        requestAnimationFrame(check);
      };

      check();
    });

  const beginTour = async () => {
    const validSteps = steps.filter((step) => step.element);

    if (!validSteps.length) {
      if (typeof onDismiss === "function") onDismiss();
      return null;
    }

    const ready = await waitForVisibleElements([validSteps[0].element]);
    const firstTarget = document.querySelector(validSteps[0].element);
    if (!ready || !firstTarget) {
      if (typeof onDismiss === "function") onDismiss();
      return null;
    }

    startDriverTour(validSteps);
    return activeGuideDriver;
  };

  const startDriverTour = (validSteps) => {
    let tourFinished = false;
    let tourAction = "got-it";
    const multi = validSteps.length > 1;

    const refreshDriverPosition = (driverInstance) => {
      if (driverInstance && typeof driverInstance.refresh === "function") {
        driverInstance.refresh();
      }
    };

    const driverSteps = validSteps.map((step, index, arr) => {
      const isLast = index === arr.length - 1;
      const popover = {
        title: step.title,
        description: step.description,
        side: step.side || side,
        align: step.align || "start",
      };

      if (!isLast) {
        popover.nextBtnText = "Next";
        popover.prevBtnText = "Previous";
      }

      if (typeof step.onNextClick === "function") {
        popover.onNextClick = step.onNextClick;
      }
      if (typeof step.onPrevClick === "function") {
        popover.onPrevClick = step.onPrevClick;
      }

      const driverStep = { element: step.element, popover };
      if (typeof step.onHighlighted === "function") {
        driverStep.onHighlighted = step.onHighlighted;
      }
      if (typeof step.onDeselected === "function") {
        driverStep.onDeselected = step.onDeselected;
      }

      return driverStep;
    });

    const setupChecklistNavigation = (popoverDom, driverInstance, state) => {
      if (!popoverDom) return;

      const footer = popoverDom.footer;
      const nav = popoverDom.footerButtons;
      if (!footer || !nav) return;

      const isLastTourStep = state.activeIndex === driverSteps.length - 1;
      const resolvedUserId =
        userId ||
        localStorage.getItem("userId") ||
        localStorage.getItem("userid") ||
        "guest";
      const resolvedGuideId = guideId || getActiveQuickGuide();

      footer.style.display = "flex";
      if (popoverDom.progress) {
        popoverDom.progress.style.display = multi ? "block" : "none";
      }

      // Hide Driver.js × — we use an explicit Close footer button instead
      if (popoverDom.closeButton) {
        popoverDom.closeButton.style.display = "none";
      }

      const makeButton = (label, className, action, { complete = true } = {}) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = className;
        button.textContent = label;
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          tourFinished = complete;
          tourAction = action;
          driverInstance.destroy();
        });
        return button;
      };

      if (!isLastTourStep) {
        if (!nav.querySelector(".quick-guide-close-btn")) {
          nav.appendChild(
            makeButton("Close", "quick-guide-close-btn", "close", {
              complete: true,
            })
          );
        }
        return;
      }

      nav.innerHTML = "";
      nav.classList.add("quick-guide-footer");
      nav.style.display = "flex";
      nav.style.gap = "8px";

      nav.appendChild(
        makeButton("Close", "quick-guide-close-btn", "close", { complete: true })
      );
      nav.appendChild(
        makeButton("Got it", "quick-guide-got-it-btn", "got-it", { complete: true })
      );

      refreshDriverPosition(driverInstance);
    };

    const tourGuideId = guideId || getActiveQuickGuide();
    const tourUserId =
      userId ||
      localStorage.getItem("userId") ||
      localStorage.getItem("userid") ||
      "guest";

    let driver;
    driver = createAppTour({
      showProgress: multi,
      progressText: "{{current}} of {{total}}",
      showButtons: ["next", "previous"],
      nextBtnText: "Next",
      prevBtnText: "Previous",
      doneBtnText: "Got it",
      allowClose: false,
      // Overlay / side click must not dismiss while a Quick Guide is active
      overlayClickBehavior: () => {},
      onHighlighted: () => {
        refreshDriverPosition(driver);
        setTimeout(() => refreshDriverPosition(driver), 120);
        setTimeout(() => refreshDriverPosition(driver), 350);
      },
      onPopoverRender: (popover, { state }) => {
        const isLastTourStep = state.activeIndex === driverSteps.length - 1;
        if (!isLastTourStep && popover.footer) {
          popover.footer.style.display = "flex";
        }
        setupChecklistNavigation(popover, driver, state);
      },
      onDestroyed: () => {
        activeGuideDriver = null;
        if (!tourFinished) {
          if (typeof onDismiss === "function") onDismiss();
          return;
        }

        if (tourUserId && tourGuideId) {
          completeQuickGuide(tourUserId, tourGuideId);
        }

        if (typeof onComplete === "function") onComplete();
        enableQuickGuideReopen();
        setTimeout(() => {
          navigateToTeamAndOpenQuickGuides(quickGuideRouter);
        }, 200);
      },
      steps: driverSteps,
    });

    activeGuideDriver = driver;
    driver.drive();
    refreshDriverPosition(driver);
    setTimeout(() => refreshDriverPosition(driver), 200);
    setTimeout(() => refreshDriverPosition(driver), 500);
    return driver;
  };

  if (startDelay > 0) {
    setTimeout(() => {
      beginTour();
    }, startDelay);
    return null;
  }

  beginTour();
  return activeGuideDriver;
}

export function runQuickGuideSpotlight({
  element,
  title,
  description,
  steps,
  onComplete,
  onDismiss,
  side = "bottom",
  align = "start",
  userId,
  guideId,
  startDelay,
}) {
  if (Array.isArray(steps)) {
    return runQuickGuideTour({
      steps,
      onComplete,
      onDismiss,
      side,
      userId,
      guideId,
      startDelay,
    });
  }

  return runQuickGuideTour({
    steps: [{ element, title, description, side, align }],
    onComplete,
    onDismiss,
    side,
    userId,
    guideId,
    startDelay,
  });
}
